/**
 * Proportion Calculator Tab - Rule of three for cost calculation
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert, HelpTooltip, HelpSteps, HelpGuide } from '../../components/ui/HelpGuide';
import { 
  Scale, 
  ArrowRight,
  Plus,
  Package,
  DollarSign,
  Droplets
} from 'lucide-react';

const UNITS = [
  { value: 'unit', label: 'Unidade' },
  { value: 'liter', label: 'Litro' },
  { value: 'kg', label: 'Quilograma' },
  { value: 'g', label: 'Grama' },
  { value: 'ml', label: 'Mililitro' },
  { value: 'pack', label: 'Pacote' },
  { value: 'box', label: 'Caixa' },
];

export default function ProportionCalculator({ categories, formatCurrency }) {
  const [loading, setLoading] = useState(false);
  
  // Input (ingredient)
  const [inputName, setInputName] = useState('');
  const [inputQuantity, setInputQuantity] = useState(1);
  const [inputUnit, setInputUnit] = useState('liter');
  const [inputCost, setInputCost] = useState(0);
  
  // Output (production)
  const [outputQuantity, setOutputQuantity] = useState(100);
  const [outputUnit, setOutputUnit] = useState('unit');
  
  // Target
  const [targetType, setTargetType] = useState('category');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [variationName, setVariationName] = useState('');
  const [createVariation, setCreateVariation] = useState(true);
  
  // Result
  const [result, setResult] = useState(null);

  const handleCalculate = async () => {
    if (!inputName || inputQuantity <= 0 || inputCost <= 0 || outputQuantity <= 0) {
      toast.error('Preencha todos os campos corretamente');
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        input_product_name: inputName,
        input_quantity: inputQuantity,
        input_unit: inputUnit,
        input_cost: inputCost,
        output_quantity: outputQuantity,
        output_unit: outputUnit,
        target_type: targetType,
        create_variation: createVariation
      };
      
      if (targetType === 'category' && targetCategoryId) {
        payload.target_category_id = targetCategoryId;
      }
      
      if (variationName) {
        payload.variation_name = variationName;
      }
      
      const response = await api.post('/pricing/calculate/proportion', payload);
      
      if (response.data?.success) {
        setResult(response.data.data);
        toast.success('Cálculo realizado com sucesso!');
        
        if (response.data.data.variation_created) {
          toast.success('Variação de custo criada!');
        }
      }
    } catch (err) {
      toast.error('Erro ao calcular proporção');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setInputName('');
    setInputQuantity(1);
    setInputCost(0);
    setOutputQuantity(100);
    setTargetCategoryId('');
    setVariationName('');
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <HelpGuide
        title="Como funciona a Calculadora de Proporção?"
        collapsible={true}
        defaultOpen={false}
        description="Use esta calculadora para descobrir quanto custa cada unidade produzida a partir de um insumo."
        sections={[
          {
            title: "Exemplo Prático",
            content: "Se 1 litro de óleo custa R$ 10,00 e você usa 1 litro para fritar 1000 coxinhas, então o custo do óleo por coxinha é R$ 0,01",
            steps: [
              { description: "Informe o insumo: Óleo, 1 litro, R$ 10,00" },
              { description: "Informe a produção: 1000 unidades" },
              { description: "O sistema calcula: R$ 10,00 ÷ 1000 = R$ 0,01 por unidade" },
              { description: "Opcionalmente, crie uma variação de custo para aplicar aos produtos" }
            ]
          }
        ]}
        tips={[
          "A variação criada será aplicada automaticamente aos produtos da categoria selecionada",
          "Você pode recalcular quando o preço do insumo mudar - o histórico é mantido",
          "Use para custos indiretos como gás, energia, embalagens, etc."
        ]}
      />

      <div className="grid grid-cols-2 gap-6">
        {/* Input Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Droplets className="h-5 w-5" />
              1. Insumo / Ingrediente
            </CardTitle>
            <CardDescription>
              Informe o insumo que será dividido na produção
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Nome do Insumo
                <HelpTooltip>Ex: Óleo, Gás, Embalagem, Farinha</HelpTooltip>
              </Label>
              <Input
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                placeholder="Ex: Óleo de soja"
                data-testid="input-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  value={inputQuantity}
                  onChange={(e) => setInputQuantity(Number(e.target.value))}
                  min={0.01}
                  step={0.1}
                />
              </div>
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={inputUnit} onValueChange={setInputUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                Custo Total
                <HelpTooltip>Quanto você paga por esta quantidade</HelpTooltip>
              </Label>
              <Input
                type="number"
                value={inputCost}
                onChange={(e) => setInputCost(Number(e.target.value))}
                min={0}
                step={0.01}
                placeholder="R$ 0,00"
                data-testid="input-cost"
              />
            </div>
          </CardContent>
        </Card>

        {/* Output Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              2. Produção
            </CardTitle>
            <CardDescription>
              Quantas unidades são produzidas com este insumo?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Quantidade Produzida
                  <HelpTooltip>Quantas unidades você produz com o insumo informado</HelpTooltip>
                </Label>
                <Input
                  type="number"
                  value={outputQuantity}
                  onChange={(e) => setOutputQuantity(Number(e.target.value))}
                  min={1}
                  placeholder="Ex: 1000"
                  data-testid="output-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select value={outputUnit} onValueChange={setOutputUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Result Preview */}
            {inputCost > 0 && outputQuantity > 0 && (
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                <div className="text-sm text-muted-foreground mb-1">Custo por unidade:</div>
                <div className="text-3xl font-bold text-primary">
                  {formatCurrency(inputCost / outputQuantity)}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {inputQuantity} {UNITS.find(u => u.value === inputUnit)?.label} de {inputName || 'insumo'} = 
                  {outputQuantity} {UNITS.find(u => u.value === outputUnit)?.label}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Target & Variation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            3. Criar Variação de Custo (Opcional)
          </CardTitle>
          <CardDescription>
            Crie automaticamente uma variação de custo para aplicar nos produtos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="create-variation"
              checked={createVariation}
              onCheckedChange={setCreateVariation}
            />
            <Label htmlFor="create-variation">
              Criar variação de custo automaticamente
            </Label>
          </div>

          {createVariation && (
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <Label>Aplicar em</Label>
                <Select value={targetType} onValueChange={setTargetType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Produtos</SelectItem>
                    <SelectItem value="category">Categoria Específica</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {targetType === 'category' && (
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={targetCategoryId} onValueChange={setTargetCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Nome da Variação
                  <HelpTooltip>Nome que aparecerá na lista de variações</HelpTooltip>
                </Label>
                <Input
                  value={variationName}
                  onChange={(e) => setVariationName(e.target.value)}
                  placeholder={`Proporção: ${inputName || 'Insumo'}`}
                />
              </div>
            </div>
          )}

          <Button 
            onClick={handleCalculate} 
            disabled={loading || !inputName || inputCost <= 0 || outputQuantity <= 0}
            className="w-full"
            data-testid="calculate-proportion-btn"
          >
            <Scale className="h-4 w-4 mr-2" />
            {loading ? 'Calculando...' : 'Calcular e Criar Variação'}
          </Button>
        </CardContent>
      </Card>

      {/* Result */}
      {result && (
        <Card className="border-green-500/50">
          <CardHeader>
            <CardTitle className="text-green-600">Cálculo Realizado!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-green-500/10 rounded-lg">
              <div className="flex-1">
                <div className="text-sm text-muted-foreground">Insumo</div>
                <div className="font-medium">{result.calculation?.input}</div>
              </div>
              <ArrowRight className="h-5 w-5" />
              <div className="flex-1">
                <div className="text-sm text-muted-foreground">Produção</div>
                <div className="font-medium">{result.calculation?.output}</div>
              </div>
              <ArrowRight className="h-5 w-5" />
              <div className="flex-1 text-right">
                <div className="text-sm text-muted-foreground">Custo por Unidade</div>
                <Badge className="text-lg bg-green-500">
                  {formatCurrency(result.calculation?.cost_per_unit || 0)}
                </Badge>
              </div>
            </div>

            {result.variation_created && (
              <HelpAlert variant="tip" className="mt-4">
                Variação de custo "{result.proportion?.variation_name}" criada com sucesso!
                Ela será aplicada automaticamente nos cálculos de precificação.
              </HelpAlert>
            )}

            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={resetForm}>
                Novo Cálculo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
